import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { financialSnapshotAPI, operationalCostsAPI } from '@/shared/lib/api';
import { OperationalCostAuditDialog } from '../components/OperationalCostAuditDialog';
import { PeriodToggle, YearSelect } from '../components/AnnualBarChart';
import { OperationalCostsAnnualView } from '../components/OperationalCostsAnnualView';
import {
  FREQUENCY_BADGE_CLASSES,
  FREQUENCY_INTERVAL_MONTHS,
  FREQUENCY_LABELS,
  FREQUENCY_OPTIONS,
  nextOccurrences,
  type RecurrenceFrequency,
} from '../utils/recurrence';
import { formatCurrency } from '@/shared/lib/utils';
import { Card, CardContent } from '@travellegacy/nexops-design-system';
import { Button } from '@travellegacy/nexops-design-system';
import { Input } from '@travellegacy/nexops-design-system';
import { Textarea } from '@travellegacy/nexops-design-system';
import { Select } from '@travellegacy/nexops-design-system';
import { Checkbox } from '@travellegacy/nexops-design-system';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@travellegacy/nexops-design-system';
import { ConfirmDialog } from '@travellegacy/nexops-design-system';
import { TabFilter } from '@travellegacy/nexops-design-system';
import { MonthYearSelector } from '@travellegacy/nexops-design-system';
import {
  SortableTable,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@travellegacy/nexops-design-system';
import {
  Plus,
  Pencil,
  Trash2,
  Loader2,
  Wrench,
  PartyPopper,
  Building2,
  Headphones,
  Megaphone,
  Settings,
  AlertCircle,
  Receipt,
  History,
  Lock,
} from 'lucide-react';

type OperationalCostCategory = 'TOOLS' | 'EVENTS' | 'OFFICE' | 'SERVICES' | 'MARKETING' | 'REFUNDS' | 'TAXES';

interface OperationalCost {
  id: string;
  title: string;
  description?: string;
  category: OperationalCostCategory;
  amount: number;
  paymentDate: string;
  paymentMethod?: string;
  notes?: string;
  isRecurring: boolean;
  recurringDayOfMonth?: number;
  recurrenceFrequency?: RecurrenceFrequency | null;
  parentCostId?: string;
  parentCost?: { recurrenceFrequency?: RecurrenceFrequency | null } | null;
  createdAt: string;
}

const categoryLabels: Record<OperationalCostCategory, string> = {
  TOOLS: 'Ferramentas',
  EVENTS: 'Eventos',
  OFFICE: 'Escritório',
  SERVICES: 'Serviços',
  MARKETING: 'Marketing',
  REFUNDS: 'Prejuízos e Reembolsos',
  TAXES: 'Impostos e Encargos',
};

const categoryIcons: Record<OperationalCostCategory, React.ElementType> = {
  TOOLS: Wrench,
  EVENTS: PartyPopper,
  OFFICE: Building2,
  SERVICES: Headphones,
  MARKETING: Megaphone,
  REFUNDS: AlertCircle,
  TAXES: Receipt,
};

function getApiErrorMessage(error: unknown, fallback: string): string {
  const data = (error as { response?: { data?: { error?: { message?: string } } } })?.response?.data;
  return data?.error?.message || fallback;
}

export default function OperationalCostsPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const now = new Date();
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [viewMode, setViewMode] = useState<'month' | 'year'>('month');
  const [categoryFilter, setCategoryFilter] = useState<'all' | OperationalCostCategory>('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCost, setEditingCost] = useState<OperationalCost | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [costToDelete, setCostToDelete] = useState<OperationalCost | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [auditTarget, setAuditTarget] = useState<{ costId?: string; title?: string } | null>(null);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'TOOLS' as OperationalCostCategory,
    amount: 0,
    paymentDate: '',
    paymentMethod: '',
    notes: '',
    isRecurring: false,
    recurrenceFrequency: 'MONTHLY' as RecurrenceFrequency,
    recurringDayOfMonth: 1,
  });

  // Estado para o valor formatado como moeda
  const [amountDisplay, setAmountDisplay] = useState('');

  // Função para formatar valor como moeda
  const formatCurrencyInput = (value: string): string => {
    // Remove tudo que não é dígito
    const numbers = value.replace(/\D/g, '');

    if (!numbers) return '';

    // Converte para número (considerando centavos)
    const amount = parseInt(numbers) / 100;

    // Formata como moeda brasileira
    return amount.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  // Função para converter display para número
  const parseCurrencyInput = (value: string): number => {
    if (!value) return 0;
    // Remove pontos de milhar e substitui vírgula por ponto
    const numbers = value.replace(/\./g, '').replace(',', '.');
    return parseFloat(numbers) || 0;
  };

  // Handler para mudança no input de valor
  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    const formatted = formatCurrencyInput(inputValue);
    setAmountDisplay(formatted);
    setFormData({ ...formData, amount: parseCurrencyInput(formatted) });
  };

  // Query para listar custos (com filtro)
  const { data: costsResponse, isLoading } = useQuery({
    queryKey: ['operational-costs', selectedMonth, selectedYear, categoryFilter],
    queryFn: async () => {
      const response = await operationalCostsAPI.list({
        month: selectedMonth,
        year: selectedYear,
        category: categoryFilter === 'all' ? undefined : categoryFilter,
      });
      return response.data;
    },
  });

  // Query para buscar todos os custos do mês (para contar as categorias)
  const { data: allCostsResponse } = useQuery({
    queryKey: ['operational-costs-all', selectedMonth, selectedYear],
    queryFn: async () => {
      const response = await operationalCostsAPI.list({
        month: selectedMonth,
        year: selectedYear,
      });
      return response.data;
    },
  });

  const costs = (costsResponse?.data || []) as OperationalCost[];
  const allCosts = (allCostsResponse?.data || []) as OperationalCost[];

  // Meses fechados (YYYY-MM): despesas deles não podem ser criadas, editadas nem excluídas
  const { data: closedMonths } = useQuery({
    queryKey: ['financial-snapshots', 'closed'],
    queryFn: async () => {
      const response = await financialSnapshotAPI.listSnapshots({ isClosed: true });
      const snapshots = (response.data.data || []) as { month: number; year: number }[];
      return new Set(snapshots.map((s) => `${s.year}-${String(s.month).padStart(2, '0')}`));
    },
    staleTime: 5 * 60 * 1000,
  });
  const isDateInClosedMonth = (isoDate: string) => !!isoDate && !!closedMonths?.has(isoDate.slice(0, 7));
  const isSelectedMonthClosed = !!closedMonths?.has(`${selectedYear}-${String(selectedMonth).padStart(2, '0')}`);

  // Mutation para criar custo
  const createCost = useMutation({
    mutationFn: (data: typeof formData) => operationalCostsAPI.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['operational-costs'] });
      queryClient.invalidateQueries({ queryKey: ['operational-costs-total'] });
      queryClient.invalidateQueries({ queryKey: ['operational-costs-by-category'] });
      handleCloseModal();
    },
    onError: (error) => setFormError(getApiErrorMessage(error, 'Erro ao criar despesa.')),
  });

  // Mutation para atualizar custo
  const updateCost = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<typeof formData> }) =>
      operationalCostsAPI.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['operational-costs'] });
      queryClient.invalidateQueries({ queryKey: ['operational-costs-total'] });
      queryClient.invalidateQueries({ queryKey: ['operational-costs-by-category'] });
      handleCloseModal();
    },
    onError: (error) => setFormError(getApiErrorMessage(error, 'Erro ao atualizar despesa.')),
  });

  // Mutation para deletar custo
  const deleteCost = useMutation({
    mutationFn: (id: string) => operationalCostsAPI.delete(id),
    onSuccess: () => {
      setActionError(null);
      queryClient.invalidateQueries({ queryKey: ['operational-costs'] });
      queryClient.invalidateQueries({ queryKey: ['operational-costs-total'] });
      queryClient.invalidateQueries({ queryKey: ['operational-costs-by-category'] });
    },
    onError: (error) => setActionError(getApiErrorMessage(error, 'Erro ao excluir despesa.')),
  });

  const handleOpenModal = (cost?: OperationalCost) => {
    setFormError(null);
    if (cost) {
      setEditingCost(cost);
      setAmountDisplay(cost.amount.toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }));
      // Se tem parentCostId, é um custo gerado (filho), então é recorrente
      const isRecurring = cost.isRecurring || !!cost.parentCostId;

      // Se não tem recurringDayOfMonth mas é recorrente, extrair da paymentDate
      let dayOfMonth = cost.recurringDayOfMonth;
      if (!dayOfMonth && isRecurring) {
        const date = new Date(cost.paymentDate);
        dayOfMonth = date.getDate();
      }

      setFormData({
        title: cost.title,
        description: cost.description || '',
        category: cost.category,
        amount: cost.amount,
        paymentDate: cost.paymentDate.split('T')[0],
        paymentMethod: cost.paymentMethod || '',
        notes: cost.notes || '',
        isRecurring: isRecurring,
        recurrenceFrequency: 'MONTHLY',
        recurringDayOfMonth: dayOfMonth || 1,
      });
    } else {
      setEditingCost(null);
      setAmountDisplay('');
      setFormData({
        title: '',
        description: '',
        category: 'TOOLS',
        amount: 0,
        paymentDate: '',
        paymentMethod: '',
        notes: '',
        isRecurring: false,
        recurrenceFrequency: 'MONTHLY',
        recurringDayOfMonth: 1,
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingCost(null);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingCost) {
      // Edição vale só para este lançamento; a recorrência é editada em Gerenciar Recorrentes
      const { title, description, category, amount, paymentDate, paymentMethod, notes } = formData;
      updateCost.mutate({
        id: editingCost.id,
        data: { title, description, category, amount, paymentDate, paymentMethod, notes },
      });
    } else {
      createCost.mutate(formData);
    }
  };

  const handleDelete = (cost: OperationalCost) => {
    setCostToDelete(cost);
    setDeleteDialogOpen(true);
  };

  // A origem de uma recorrência não é excluída aqui: encerrar mantém o histórico
  const isRecurrenceOrigin = !!costToDelete?.isRecurring;

  const confirmDelete = () => {
    if (isRecurrenceOrigin) {
      setDeleteDialogOpen(false);
      navigate('/financial/manage-recurring');
      return;
    }
    if (costToDelete) {
      deleteCost.mutate(costToDelete.id);
      setDeleteDialogOpen(false);
      setCostToDelete(null);
    }
  };

  // Periodicidade do lançamento: a da própria recorrência ou a da recorrência que o gerou
  // (recorrências antigas, sem periodicidade gravada, são mensais)
  const getCostFrequency = (cost: OperationalCost): RecurrenceFrequency | null => {
    if (cost.isRecurring) return cost.recurrenceFrequency ?? 'MONTHLY';
    if (cost.parentCostId) return cost.parentCost?.recurrenceFrequency ?? 'MONTHLY';
    return null;
  };

  const getCostTypeLabel = (cost: OperationalCost): string => {
    const frequency = getCostFrequency(cost);
    return frequency ? `Recorrente ${FREQUENCY_LABELS[frequency].toLowerCase()}` : 'Única';
  };

  // Badge do tipo: única ou recorrente com a periodicidade
  const getCostTypeBadge = (cost: OperationalCost) => {
    const frequency = getCostFrequency(cost);
    if (frequency) {
      return (
        <span className={`text-xs px-2 py-1 rounded font-medium whitespace-nowrap ${FREQUENCY_BADGE_CLASSES[frequency]}`}>
          {getCostTypeLabel(cost)}
        </span>
      );
    }
    return (
      <span className="text-xs bg-gray-100 dark:bg-gray-900/20 text-gray-600 dark:text-gray-400 px-2 py-1 rounded">
        Única
      </span>
    );
  };

  const totalCosts = costs.reduce((sum, cost) => sum + cost.amount, 0);
  // Quanto do total vem de recorrências (inclui lançamentos gerados por elas) e quanto é avulso
  const recurringTotal = costs
    .filter((cost) => getCostFrequency(cost))
    .reduce((sum, cost) => sum + cost.amount, 0);
  const singleTotal = totalCosts - recurringTotal;

  // Contar custos por categoria (usando todos os custos do mês, não os filtrados)
  const categoryCounts = {
    all: allCosts.length,
    TOOLS: allCosts.filter((c) => c.category === 'TOOLS').length,
    EVENTS: allCosts.filter((c) => c.category === 'EVENTS').length,
    OFFICE: allCosts.filter((c) => c.category === 'OFFICE').length,
    SERVICES: allCosts.filter((c) => c.category === 'SERVICES').length,
    MARKETING: allCosts.filter((c) => c.category === 'MARKETING').length,
    REFUNDS: allCosts.filter((c) => c.category === 'REFUNDS').length,
    TAXES: allCosts.filter((c) => c.category === 'TAXES').length,
  };

  // Nome do mês
  const monthNames = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
  ];
  const monthName = monthNames[selectedMonth - 1];

  // Cabeçalho com alternância Mensal | Anual
  const header = (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
      <div>
        <h1 className="text-3xl font-bold">Despesas</h1>
        <p className="text-muted-foreground mt-1">
          {viewMode === 'year' ? 'Despesas do ano, mês a mês, por categoria' : 'Gerencie as despesas da empresa'}
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <PeriodToggle value={viewMode} onChange={setViewMode} />
        {viewMode === 'year' ? (
          <YearSelect value={selectedYear} onChange={setSelectedYear} />
        ) : (
          <MonthYearSelector
            selectedMonth={selectedMonth}
            selectedYear={selectedYear}
            onMonthChange={setSelectedMonth}
            onYearChange={setSelectedYear}
          />
        )}
      </div>
    </div>
  );

  // Visão anual: clicar num mês abre a visão mensal dele
  if (viewMode === 'year') {
    return (
      <div className="space-y-6">
        {header}
        <OperationalCostsAnnualView
          year={selectedYear}
          onSelectMonth={(month) => {
            setSelectedMonth(month);
            setViewMode('month');
          }}
        />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {header}

      {/* Custos Registrados */}
      <Card>
        <CardContent className="pt-6">
          <div className="space-y-4">
            {/* Total e Botões */}
            <div className="flex items-center justify-between">
              <div className="flex items-baseline gap-2">
                <span className="text-2xl font-bold text-foreground">{monthName}:</span>
                <p className="text-2xl font-bold text-primary">{formatCurrency(totalCosts)}</p>
                <span className="text-sm text-muted-foreground">
                  (Recorrentes {formatCurrency(recurringTotal)} · Únicas {formatCurrency(singleTotal)})
                </span>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setAuditTarget({})}>
                  <History className="h-4 w-4 mr-2" />
                  Histórico
                </Button>
                <Button variant="outline" onClick={() => navigate('/financial/manage-recurring')}>
                  <Settings className="h-4 w-4 mr-2" />
                  Gerenciar Recorrentes
                </Button>
                <Button onClick={() => handleOpenModal()}>
                  <Plus className="h-4 w-4 mr-2" />
                  Nova Despesa
                </Button>
              </div>
            </div>

            {isSelectedMonthClosed && (
              <div className="flex items-center gap-2 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 px-4 py-3 text-sm text-amber-800 dark:text-amber-200">
                <Lock className="h-4 w-4 shrink-0" />
                {monthName} está fechado: os lançamentos deste mês não podem ser criados, editados nem excluídos.
              </div>
            )}

            {actionError && (
              <div className="flex items-center gap-2 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-4 py-3 text-sm text-red-700 dark:text-red-300">
                <AlertCircle className="h-4 w-4 shrink-0" />
                {actionError}
              </div>
            )}

            {/* Filtros por Categoria */}
            <TabFilter
              tabs={[
                { value: 'all', label: 'Todas', count: categoryCounts.all },
                { value: 'TOOLS', label: 'Ferramentas', count: categoryCounts.TOOLS },
                { value: 'EVENTS', label: 'Eventos', count: categoryCounts.EVENTS },
                { value: 'OFFICE', label: 'Escritório', count: categoryCounts.OFFICE },
                { value: 'SERVICES', label: 'Serviços', count: categoryCounts.SERVICES },
                { value: 'MARKETING', label: 'Marketing', count: categoryCounts.MARKETING },
                { value: 'REFUNDS', label: 'Prejuízos e Reembolsos', count: categoryCounts.REFUNDS },
                { value: 'TAXES', label: 'Impostos e Encargos', count: categoryCounts.TAXES },
              ]}
              currentTab={categoryFilter}
              onChange={(value) => setCategoryFilter(value as typeof categoryFilter)}
            />

            {/* Tabela */}
            {costs.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                Nenhuma despesa registrada para este período
              </div>
            ) : (
              <SortableTable
                data={costs.map((cost) => ({ ...cost, typeLabel: getCostTypeLabel(cost) }))}
                defaultSortKey="paymentDate"
                defaultSortDirection="desc"
              >
                {(sortedCosts) => (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead sortable sortKey="paymentDate">Data</TableHead>
                        <TableHead sortable sortKey="title">Título</TableHead>
                        <TableHead sortable sortKey="category">Categoria</TableHead>
                        <TableHead sortable sortKey="amount" align="right">Valor</TableHead>
                        <TableHead sortable sortKey="typeLabel">Tipo</TableHead>
                        <TableHead align="right">Ações</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sortedCosts.map((cost) => {
                        const CategoryIcon = categoryIcons[cost.category];
                        return (
                          <TableRow key={cost.id}>
                            <TableCell>
                              {cost.paymentDate.split('T')[0].split('-').reverse().join('/')}
                            </TableCell>
                            <TableCell>
                              <div>
                                <p className="font-medium">{cost.title}</p>
                                {cost.description && (
                                  <p className="text-sm text-muted-foreground">{cost.description}</p>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <CategoryIcon className="h-4 w-4" />
                                <span>{categoryLabels[cost.category]}</span>
                              </div>
                            </TableCell>
                            <TableCell className="text-right font-medium">
                              {formatCurrency(cost.amount)}
                            </TableCell>
                            <TableCell>
                              {getCostTypeBadge(cost)}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center justify-end gap-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setAuditTarget({ costId: cost.id, title: cost.title })}
                                  title="Histórico de alterações"
                                  aria-label="Histórico de alterações"
                                >
                                  <History className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleOpenModal(cost)}
                                  disabled={isDateInClosedMonth(cost.paymentDate)}
                                  title={isDateInClosedMonth(cost.paymentDate) ? 'Mês fechado' : 'Editar'}
                                >
                                  <Pencil className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="destructive"
                                  size="sm"
                                  onClick={() => handleDelete(cost)}
                                  disabled={isDateInClosedMonth(cost.paymentDate)}
                                  title={isDateInClosedMonth(cost.paymentDate) ? 'Mês fechado' : 'Excluir'}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </SortableTable>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Modal Criar/Editar */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingCost ? 'Editar Despesa' : 'Nova Despesa'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 px-6">
            <Input
              id="title"
              label="Título *"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
            />

            <div className="grid grid-cols-2 gap-4">
              <Select
                id="category"
                label="Categoria *"
                value={formData.category}
                onChange={(e) =>
                  setFormData({ ...formData, category: e.target.value as OperationalCostCategory })
                }
                options={Object.entries(categoryLabels).map(([value, label]) => ({
                  value,
                  label,
                }))}
                required
              />
              <div className="space-y-2">
                <label htmlFor="amount" className="text-sm font-medium">
                  Valor *
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                    R$
                  </span>
                  <Input
                    id="amount"
                    type="text"
                    value={amountDisplay}
                    onChange={handleAmountChange}
                    className="pl-10"
                    placeholder="0,00"
                    required
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Input
                id="paymentDate"
                label="Data de Pagamento *"
                type="date"
                value={formData.paymentDate}
                onChange={(e) => setFormData({ ...formData, paymentDate: e.target.value })}
                required
              />
              <Select
                id="paymentMethod"
                label="Forma de Pagamento"
                value={formData.paymentMethod}
                onChange={(e) => setFormData({ ...formData, paymentMethod: e.target.value })}
                options={[
                  { value: '', label: 'Selecione...' },
                  { value: 'PIX', label: 'PIX' },
                  { value: 'Cartão de Crédito', label: 'Cartão de Crédito' },
                  { value: 'Cartão de Débito', label: 'Cartão de Débito' },
                  { value: 'Boleto', label: 'Boleto' },
                  { value: 'Transferência', label: 'Transferência' },
                  { value: 'Dinheiro', label: 'Dinheiro' },
                ]}
              />
            </div>

            <Textarea
              id="description"
              label="Descrição"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              placeholder="Adicione detalhes sobre esta despesa..."
            />

            {/* Recorrência: definida na criação; depois é editada em Gerenciar Recorrentes */}
            {editingCost ? (
              (editingCost.isRecurring || editingCost.parentCostId) && (
                <div className="rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 p-4 text-sm text-blue-800 dark:text-blue-200">
                  Este lançamento faz parte de uma despesa recorrente. As alterações valem só para ele.
                  Para mudar valor, periodicidade ou encerrar a recorrência, use{' '}
                  <button
                    type="button"
                    className="font-medium underline"
                    onClick={() => navigate('/financial/manage-recurring')}
                  >
                    Gerenciar Recorrentes
                  </button>
                  .
                </div>
              )
            ) : (
              <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 space-y-4 bg-gray-50 dark:bg-gray-900/20">
                <div className="flex items-center gap-3">
                  <Checkbox
                    id="isRecurring"
                    checked={formData.isRecurring}
                    onChange={(checked) => setFormData({ ...formData, isRecurring: checked })}
                  />
                  <label htmlFor="isRecurring" className="text-sm font-medium cursor-pointer">
                    Despesa Recorrente
                  </label>
                </div>

                {formData.isRecurring && (
                  <div className="space-y-4 pt-2 border-t border-gray-200 dark:border-gray-700">
                    <div className="grid grid-cols-2 gap-4">
                      <Select
                        id="recurrenceFrequency"
                        label="Periodicidade *"
                        value={formData.recurrenceFrequency}
                        onChange={(e) =>
                          setFormData({ ...formData, recurrenceFrequency: e.target.value as RecurrenceFrequency })
                        }
                        options={FREQUENCY_OPTIONS}
                      />
                      {formData.recurrenceFrequency === 'MONTHLY' ? (
                        <Input
                          id="recurringDayOfMonth"
                          label="Dia do Mês *"
                          type="number"
                          min="1"
                          max="31"
                          value={formData.recurringDayOfMonth}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              recurringDayOfMonth: parseInt(e.target.value) || 1,
                            })
                          }
                          required
                        />
                      ) : (
                        <div className="flex items-end pb-2">
                          <p className="text-xs text-muted-foreground">
                            Repete a cada {FREQUENCY_INTERVAL_MONTHS[formData.recurrenceFrequency]} meses no mesmo dia
                            da data de pagamento.
                          </p>
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Se o dia não existir no mês (ex: 31 em fevereiro), será usado o último dia. Os lançamentos
                      ficam gerados sempre 12 meses à frente.
                    </p>

                    {/* Preview dos próximos lançamentos */}
                    {formData.paymentDate && (
                      <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
                        <p className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-3">
                          Próximos lançamentos ({FREQUENCY_LABELS[formData.recurrenceFrequency].toLowerCase()})
                        </p>
                        <div className="grid grid-cols-3 gap-2 text-xs text-blue-700 dark:text-blue-300">
                          {nextOccurrences(
                            formData.paymentDate,
                            formData.recurrenceFrequency,
                            formData.recurrenceFrequency === 'MONTHLY'
                              ? formData.recurringDayOfMonth
                              : Number(formData.paymentDate.slice(8, 10)),
                            3
                          ).map((date) => (
                            <div key={date} className="bg-white/50 dark:bg-black/20 px-2 py-1.5 rounded font-medium">
                              {date}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {isDateInClosedMonth(formData.paymentDate) && (
              <p className="text-sm text-amber-700 dark:text-amber-300">
                A data de pagamento está em um mês fechado. Escolha uma data em um mês aberto.
              </p>
            )}

            {formError && <p className="text-sm text-red-600 dark:text-red-400">{formError}</p>}

            <DialogFooter>
              <div className="flex gap-2 pt-4">
                <Button type="button" variant="outline" onClick={handleCloseModal}>
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={createCost.isPending || updateCost.isPending || isDateInClosedMonth(formData.paymentDate)}
                >
                  {createCost.isPending || updateCost.isPending ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Salvando...
                    </>
                  ) : editingCost ? (
                    'Atualizar'
                  ) : (
                    'Criar'
                  )}
                </Button>
              </div>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Histórico de alterações */}
      <OperationalCostAuditDialog
        open={!!auditTarget}
        onOpenChange={(open) => !open && setAuditTarget(null)}
        costId={auditTarget?.costId}
        title={auditTarget?.title}
      />

      {/* Modal de Confirmação de Exclusão */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title={isRecurrenceOrigin ? 'Despesa recorrente' : 'Excluir Despesa'}
        description={
          isRecurrenceOrigin
            ? 'Este é o primeiro lançamento de uma despesa recorrente. Para parar os próximos lançamentos ' +
              'mantendo o histórico, encerre a recorrência em Gerenciar Recorrentes.'
            : costToDelete?.parentCostId
              ? 'Excluir só este lançamento? Os demais lançamentos da recorrência continuam e ele não será recriado. ' +
                'Esta ação não pode ser desfeita.'
              : 'Tem certeza que deseja deletar esta despesa? Esta ação não pode ser desfeita.'
        }
        confirmText={isRecurrenceOrigin ? 'Ir para Gerenciar Recorrentes' : 'Excluir'}
        cancelText="Cancelar"
        variant={isRecurrenceOrigin ? 'default' : 'destructive'}
        icon={isRecurrenceOrigin ? undefined : 'delete'}
        onConfirm={confirmDelete}
        isLoading={deleteCost.isPending}
      />
    </div>
  );
}
